import React, {useEffect, useState} from 'react';
import {
    DiffSummary,
    SuggestedProblemEditMinimal,
    SuggestedProblemEditStatusEnum,
    SuggestedProblemEditsCriteria,
} from '@types';
import {
    useDeleteSuggestedProblemEditMutation,
    useFetchSuggestedProblemEditsQuery,
    useCountSuggestedProblemEditsQuery,
} from '@api';
import dayjs from 'dayjs';
import {toast} from 'react-toastify';
import {ConfirmModal} from './modals/ConfirmModal';
import {CustomPagination, Loading, NothingHere, UserItem} from '@components';
import {useSelector, canEditProblem} from '@utils';
import {NumberParam, useQueryParam} from 'use-query-params';
import RankText from './common/RankText';
import {isEmpty} from 'lodash';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faFlag} from '@fortawesome/free-regular-svg-icons';
import {Link, useNavigate} from 'react-router';
import {Table} from '@goproblems/ui';
import {useTranslation} from 'react-i18next';
import {useTheme} from '@components/theme/useTheme';
import {ProblemSuggestionsList} from './suggestions/ProblemSuggestionsList';
import {
    SuggestionStatusFilters,
    type SuggestionListLayout,
} from './suggestions/SuggestionEntry';

const StatusBadge = ({status}: {status: SuggestedProblemEditStatusEnum}) => {
    const {t} = useTranslation();
    const statusClass: Record<SuggestedProblemEditStatusEnum, string> = {
        [SuggestedProblemEditStatusEnum.OPEN]:
            'bg-blue-500 text-white px-2 py-1 rounded-sm text-sm uppercase',
        [SuggestedProblemEditStatusEnum.APPROVED]:
            'bg-green-600 text-white px-2 py-1 rounded-sm text-sm uppercase',
        [SuggestedProblemEditStatusEnum.REJECTED]:
            'bg-red-500 text-white px-2 py-1 rounded-sm text-sm uppercase',
    };
    const statusLabel: Record<SuggestedProblemEditStatusEnum, string> = {
        [SuggestedProblemEditStatusEnum.OPEN]: t('common.open'),
        [SuggestedProblemEditStatusEnum.APPROVED]: t('common.approved'),
        [SuggestedProblemEditStatusEnum.REJECTED]: t('common.rejected'),
    };

    return <span className={statusClass[status]}>{statusLabel[status]}</span>;
};

export const SuggestionsDataList = ({
    criteria: criteriaProp = {},
    header,
    embedded = false,
    renderLayout,
    showDeleteButton = true,
    showEditButton = true,
    showResolveButton = false,
    showViewButton = true,
    showSuggestedByColumn = true,
    showFlagColumn = true,
}: {
    criteria?: SuggestedProblemEditsCriteria;
    header?: string;
    embedded?: boolean;
    renderLayout?: SuggestionListLayout;
    showResolveButton?: boolean;
    showEditButton?: boolean;
    showDeleteButton?: boolean;
    showViewButton?: boolean;
    showSuggestedByColumn?: boolean;
    showFlagColumn?: boolean;
}) => {
    const {user: authUser} = useSelector(state => state.auth);
    const navigate = useNavigate();
    const theme = useTheme();

    const [deleteSuggestedProblemEdit, {isLoading: isDeleting}] =
        useDeleteSuggestedProblemEditMutation();
    const [itemToDelete, setItemToDelete] = React.useState<number | null>(null);
    const [paramProblemId] = useQueryParam('problemId', NumberParam);

    const [edits, setEdits] = useState<SuggestedProblemEditMinimal[]>([]);
    const [offset, setOffset] = useState(0);
    const [status, setStatus] = useState<SuggestedProblemEditStatusEnum>(
        SuggestedProblemEditStatusEnum.OPEN
    );

    const {t} = useTranslation();

    const textSecondaryClass =
        theme === 'dark' ? 'text-neutral-400' : 'text-neutral-600';
    const textSmallSecondaryClass = `text-sm ${textSecondaryClass}`;

    const renderDiffSummary = (diffSummary: DiffSummary | null): string => {
        if (!diffSummary) return '';

        const parts: string[] = [];

        if (diffSummary.addedNodes > 0) {
            if (diffSummary.addedVariations > 1) {
                parts.push(
                    t('suggestions.diff_summary.added_nodes_with_variations', {
                        count: diffSummary.addedNodes,
                        variations: diffSummary.addedVariations,
                    })
                );
            } else {
                parts.push(
                    t('suggestions.diff_summary.added_nodes', {
                        count: diffSummary.addedNodes,
                    })
                );
            }
        }

        if (diffSummary.deletedNodes > 0) {
            parts.push(
                t('suggestions.diff_summary.deleted_nodes', {
                    count: diffSummary.deletedNodes,
                })
            );
        }

        if (diffSummary.changedNodes > 0) {
            if (
                diffSummary.changedNodes === 1 &&
                diffSummary.changedProperties.length > 0
            ) {
                parts.push(
                    t(
                        'suggestions.diff_summary.changed_nodes_with_properties',
                        {
                            count: diffSummary.changedNodes,
                            properties:
                                diffSummary.changedProperties.join(', '),
                        }
                    )
                );
            } else {
                parts.push(
                    t('suggestions.diff_summary.changed_nodes', {
                        count: diffSummary.changedNodes,
                    })
                );
            }
        }

        return parts.length > 0
            ? parts.join(', ')
            : t('suggestions.diff_summary.no_changes');
    };

    const handlePageChange = (offset: number) => {
        setOffset(offset);
    };

    const criteria: SuggestedProblemEditsCriteria = {
        limit: 10,
        offset: 0,
        sortDirection: 'desc',
        sortBy: 'id',
        status,
        problemId: paramProblemId ?? undefined,
        ...criteriaProp,
    };

    const {
        data: suggestionsData,
        isFetching,
        refetch,
    } = useFetchSuggestedProblemEditsQuery(
        {criteria: {...criteria, offset}},
        {refetchOnMountOrArgChange: true}
    );

    const {data: suggestionsCountsData, refetch: refetchCounts} =
        useCountSuggestedProblemEditsQuery(
            {criteria: {...criteria, status: undefined}},
            {refetchOnMountOrArgChange: true}
        );

    useEffect(() => {
        if (suggestionsData) {
            setEdits(suggestionsData.entries);
        }
    }, [suggestionsData]);

    const tdClassName = (item: SuggestedProblemEditMinimal, className = '') => {
        return !isEmpty(item.comment) || !isEmpty(item.resolutionComment)
            ? `!border-b-0 ${className}`
            : `${className}`;
    };

    const filters = (
        <SuggestionStatusFilters
            value={status}
            counts={suggestionsCountsData}
            onChange={value => {
                setStatus(value);
                setOffset(0);
            }}
        />
    );

    const content = (
        <div
            className={`${embedded ? 'suggestion-list-embedded' : 'suggestion-list'} w-full flex flex-col`}>
            <ConfirmModal
                show={itemToDelete !== null}
                description={t(
                    'suggestions.are_you_sure_you_want_to_delete_this_suggestion',
                    {id: itemToDelete}
                )}
                okVariant="danger"
                okText={t('common.delete')}
                onHide={() => setItemToDelete(null)}
                onOk={async () => {
                    if (itemToDelete === null || isDeleting) return;
                    try {
                        await deleteSuggestedProblemEdit(itemToDelete).unwrap();
                        toast.success(t('suggestions.deleted_successfully'), {
                            autoClose: 1000,
                        });
                        setItemToDelete(null);
                        void refetch();
                        void refetchCounts();
                    } catch {
                        toast.error(t('suggestions.failed_to_delete'));
                    }
                }}
            />
            {embedded ? (
                !renderLayout && filters
            ) : (
                <div className="suggestion-list-heading flex flex-wrap items-center justify-between gap-3 border-bottom px-3 py-2">
                    {!embedded && (
                        <span className="font-semibold">
                            {header ?? t('suggestions.title')}
                        </span>
                    )}
                    <div
                        className="suggestion-status-filters flex flex-wrap gap-1"
                        role="group"
                        aria-label={t('suggestions.status')}>
                        {[
                            {
                                value: SuggestedProblemEditStatusEnum.OPEN,
                                count: suggestionsCountsData?.open ?? 0,
                                label: t('common.open'),
                            },
                            {
                                value: SuggestedProblemEditStatusEnum.APPROVED,
                                count: suggestionsCountsData?.approved ?? 0,
                                label: t('common.approved'),
                            },
                            {
                                value: SuggestedProblemEditStatusEnum.REJECTED,
                                count: suggestionsCountsData?.rejected ?? 0,
                                label: t('common.rejected'),
                            },
                        ].map(item => (
                            <button
                                key={item.value}
                                type="button"
                                aria-pressed={status === item.value}
                                className={`rounded px-2 py-1 text-sm transition-colors ${status === item.value ? 'bg-neutral-100 font-semibold dark:bg-[var(--ui-surface-muted)]' : 'text-muted hover:bg-neutral-50 dark:hover:bg-[var(--ui-surface-muted)]'}`}
                                onClick={() => {
                                    setStatus(item.value);
                                    setOffset(0);
                                }}>
                                {item.count} {item.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {isFetching && <Loading className="p-5" />}
            {!isFetching && edits.length === 0 && (
                <div className="suggestion-list-empty mt-4">
                    <NothingHere />
                </div>
            )}
            {!isFetching &&
                edits.length > 0 &&
                (embedded ? (
                    <ProblemSuggestionsList
                        items={edits}
                        showDeleteButton={showDeleteButton}
                        showEditButton={showEditButton}
                        showResolveButton={showResolveButton}
                        showViewButton={showViewButton}
                        showSuggestedByColumn={showSuggestedByColumn}
                        showFlagColumn={showFlagColumn}
                        isDeleting={isDeleting}
                        onDelete={setItemToDelete}
                        renderDiffSummary={renderDiffSummary}
                    />
                ) : (
                    <Table className="mb-0" responsive>
                        <thead>
                            <tr>
                                <th>{t('common.id')}</th>
                                <th>{t('common.problem')}</th>
                                <th>{t('common.author')}</th>
                                <th>{t('common.rank')}</th>
                                {showFlagColumn && <th>{t('common.flags')}</th>}
                                <th>{t('common.status')}</th>
                                {showSuggestedByColumn && (
                                    <th>{t('common.suggested_by')}</th>
                                )}
                                <th>{t('common.created_at')}</th>
                                <th>{t('common.resolved_by')}</th>
                                <th>{t('common.resolved_at')}</th>
                                <th>{t('common.action')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {edits.map(item => (
                                <React.Fragment key={item.id}>
                                    <tr>
                                        <td className={tdClassName(item)}>
                                            {item.id}
                                        </td>
                                        <td className={tdClassName(item)}>
                                            <Link
                                                to={
                                                    '/problems/' +
                                                    item.problem.id
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer">
                                                <img
                                                    src={item.problem.imageUrl}
                                                    alt={t(
                                                        'common.board_thumbnail'
                                                    )}
                                                    className="w-12 h-12"
                                                />
                                            </Link>
                                        </td>
                                        <td className={tdClassName(item)}>
                                            <UserItem
                                                showLevel={false}
                                                textClassName={'!mx-1'}
                                                avatarClassName={'!ml-0'}
                                                user={item.problem.author}
                                            />
                                        </td>
                                        <td className={tdClassName(item)}>
                                            <RankText
                                                rank={item.problem.rank}
                                            />
                                        </td>
                                        {showFlagColumn && (
                                            <td className={tdClassName(item)}>
                                                <FontAwesomeIcon
                                                    color={'#737373'}
                                                    icon={faFlag}
                                                />
                                                {item.problem.flagStats
                                                    .positiveFlagSum > 0 && (
                                                    <span>
                                                        {' '}
                                                        +
                                                        {
                                                            item.problem
                                                                .flagStats
                                                                .positiveFlagSum
                                                        }
                                                    </span>
                                                )}
                                                {item.problem.flagStats
                                                    .negativeFlagSum < 0 && (
                                                    <span>
                                                        {' '}
                                                        {
                                                            item.problem
                                                                .flagStats
                                                                .negativeFlagSum
                                                        }
                                                    </span>
                                                )}
                                            </td>
                                        )}
                                        <td
                                            className={`w-20 ${tdClassName(item)}`}>
                                            <StatusBadge status={item.status} />
                                        </td>
                                        {showSuggestedByColumn && (
                                            <td
                                                className={`min-w-[200px] ${tdClassName(item)}`}>
                                                <UserItem
                                                    user={item.user}
                                                    textClassName={'!mx-1'}
                                                    avatarClassName={'!ml-0'}
                                                />
                                            </td>
                                        )}
                                        <td className={`${tdClassName(item)}`}>
                                            {dayjs(item.createdAt).fromNow()}
                                        </td>
                                        <td className={`${tdClassName(item)}`}>
                                            {item.resolvedBy?.name || '-'}
                                        </td>
                                        <td className={`${tdClassName(item)}`}>
                                            {item.resolvedAt
                                                ? dayjs(
                                                      item.resolvedAt
                                                  ).fromNow()
                                                : '-'}
                                        </td>
                                        <td className={`${tdClassName(item)}`}>
                                            <div className="flex gap-1">
                                                {(showViewButton ||
                                                    showResolveButton) && (
                                                    <button
                                                        className={`${
                                                            canEditProblem(
                                                                authUser,
                                                                item.problem
                                                            )
                                                                ? 'bg-blue-500'
                                                                : 'bg-neutral-500'
                                                        } text-white px-2 py-1 rounded-sm text-sm`}
                                                        onClick={() => {
                                                            navigate(
                                                                `/suggestions/${item.id}`
                                                            );
                                                        }}>
                                                        {canEditProblem(
                                                            authUser,
                                                            item.problem
                                                        )
                                                            ? t('common.resolve')
                                                            : t('common.view')}
                                                    </button>
                                                )}

                                                {showEditButton &&
                                                    item.user.id ===
                                                        authUser?.id && (
                                                        <button
                                                            className="bg-blue-500 text-white px-2 py-1 rounded-sm text-sm"
                                                            onClick={() => {
                                                                navigate(
                                                                    item.status ===
                                                                        SuggestedProblemEditStatusEnum.OPEN
                                                                        ? `/suggestions/${item.id}/edit`
                                                                        : `/suggestions/${item.id}`
                                                                );
                                                            }}>
                                                            {item.status ===
                                                            SuggestedProblemEditStatusEnum.OPEN
                                                                ? t(
                                                                      'common.edit'
                                                                  )
                                                                : t(
                                                                      'common.view'
                                                                  )}
                                                        </button>
                                                    )}
                                                {showDeleteButton &&
                                                    item.status ===
                                                        SuggestedProblemEditStatusEnum.OPEN && (
                                                        <button
                                                            className="bg-red-500 text-white px-2 py-1 rounded-sm text-sm"
                                                            onClick={async () => {
                                                                if (!item.id)
                                                                    return;
                                                                setItemToDelete(
                                                                    item.id
                                                                );
                                                            }}>
                                                            {t('common.delete')}
                                                        </button>
                                                    )}
                                            </div>
                                        </td>
                                    </tr>
                                    {(!isEmpty(item.comment) ||
                                        !isEmpty(item.resolutionComment) ||
                                        item.diffSummary) && (
                                        <tr>
                                            <td></td>
                                            <td
                                                colSpan={10}
                                                className="border-t-0">
                                                <div className="flex flex-col gap-2 mb-2">
                                                    {!isEmpty(item.comment) && (
                                                        <div
                                                            className={
                                                                textSmallSecondaryClass
                                                            }>
                                                            <span className="font-semibold mr-1">
                                                                {t(
                                                                    'common.comment'
                                                                )}
                                                                :
                                                            </span>
                                                            {item.comment}
                                                        </div>
                                                    )}
                                                    {!isEmpty(
                                                        item.resolutionComment
                                                    ) && (
                                                        <div
                                                            className={
                                                                textSmallSecondaryClass
                                                            }>
                                                            <span className="font-semibold mr-1">
                                                                {t(
                                                                    'suggestions.resolution_comment'
                                                                )}
                                                                :
                                                            </span>
                                                            {
                                                                item.resolutionComment
                                                            }
                                                        </div>
                                                    )}
                                                    {item.diffSummary && (
                                                        <div
                                                            className={
                                                                textSmallSecondaryClass
                                                            }>
                                                            <span className="font-semibold mr-1">
                                                                {t(
                                                                    'suggestions.diff_summary.label'
                                                                )}
                                                                :
                                                            </span>
                                                            {renderDiffSummary(
                                                                item.diffSummary
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                ))}
            {suggestionsData && edits.length > 0 && (
                <div
                    className={
                        embedded ? 'suggestion-list-pagination' : 'mt-4 mb-2'
                    }>
                    <CustomPagination
                        handlePageChange={handlePageChange}
                        totalRecords={suggestionsData.totalRecords}
                        recordsPerPage={10}
                        offset={offset}
                    />
                </div>
            )}
        </div>
    );

    return renderLayout ? renderLayout({filters, children: content}) : content;
};
